import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { CheckIcon, ChevronRightIcon, ShieldCheckIcon, ChartBarIcon, CurrencyDollarIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';

function InvestmentPlans() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const plans = [
    {
      name: "Standard Plan",
      price: "$1,000",
      features: [
        "10x Profit in 21 days",
        "2% Welcome bonus",
        "$1,000 earn $10,000",
        "$2,000 earn $20,000",
        "$3,000 earn $30,000",
        "$4,000 earn $40,000",
        "$5,000 earn $50,000"
      ],
      duration: "21 days",
      type: "Basic portfolio",
      support: "24/7 Email Support"
    },
    {
      name: "Premium Plan",
      price: "$6,000",
      features: [
        "15x Profit in 21 days",
        "3% Welcome bonus",
        "$6,000 earn $90,000",
        "$7,000 earn $105,000",
        "$8,000 earn $120,000",
        "$9,000 earn $135,000",
        "$10,000 earn $150,000"
      ],
      duration: "21 days",
      type: "Enhanced portfolio",
      support: "24/7 Priority Chat Support",
      popular: true
    },
    {
      name: "Advanced Plan",
      price: "$10,000",
      features: [
        "20x Profit in 21 days",
        "5% Welcome bonus",
        "$10,000 earn $200,000",
        "$11,000 earn $220,000",
        "$12,000 earn $240,000",
        "$13,000 earn $260,000",
        "$14,000 earn $280,000",
        "VIP Account Manager",
        "Priority Withdrawals",
        "Exclusive Market Analysis"
      ],
      duration: "21 days",
      type: "VIP Wealth Management",
      support: "Dedicated Account Manager"
    },
    {
      name: "Platinum Plan",
      price: "$25,000",
      features: [
        "25x Profit in 21 days",
        "7% Welcome bonus",
        "$25,000 earn $625,000",
        "$30,000 earn $750,000",
        "$35,000 earn $875,000",
        "$40,000 earn $1,000,000",
        "$45,000 earn $1,125,000",
        "Elite Account Manager",
        "Instant Withdrawals",
        "Advanced Market Analysis",
        "Private Investment Events",
        "Personalized Portfolio Strategy"
      ],
      duration: "21 days",
      type: "Elite Wealth Management",
      support: "24/7 Personal Concierge"
    },
    {
      name: "Diamond Plan",
      price: "$50,000",
      features: [
        "30x Profit in 21 days",
        "10% Welcome bonus",
        "$50,000 earn $1,500,000",
        "$60,000 earn $1,800,000",
        "$70,000 earn $2,100,000",
        "$80,000 earn $2,400,000",
        "$90,000 earn $2,700,000",
        "Executive Account Manager",
        "Instant Priority Withdrawals",
        "Expert Market Analysis",
        "Exclusive Investment Events",
        "Custom Portfolio Strategy",
        "Global Investment Opportunities",
        "Wealth Preservation Consulting"
      ],
      duration: "21 days",
      type: "Executive Wealth Management",
      support: "Dedicated Executive Team"
    }
  ];

  const testimonials = [
    {
      text: "I've tried other platforms, but this one really delivers on transparency and performance. My returns have been impressive!",
      author: "Irene Blimbing"
    },
    {
      text: "The user interface is simple, and I love how my investments are laid out so clearly. Highly recommended!",
      author: "Michael Bedengan"
    },
    {
      text: "The personalized investment updates are a game-changer. I always know exactly where my money stands!",
      author: "David Knox"
    },
    {
      text: "Investing has never been easier! My portfolio has grown steadily, and the dashboard keeps me informed every step of the way.",
      author: "Angelina Kepanjen"
    }
  ];

  const handlePlanSelect = (plan) => {
    localStorage.setItem('selectedPlan', JSON.stringify(plan));
    if (currentUser) {
      navigate('/dashboard?tab=deposit');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen bg-background-paper">
      <Navigation />

      {/* Hero Section */}
      <motion.div 
        className="relative overflow-hidden bg-primary-700"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-800 to-primary-600 opacity-90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl md:text-6xl">
              Your Path to Financial Freedom
            </h1>
            <p className="mt-6 max-w-xl mx-auto text-xl text-neutral-100">
              Secure your financial future with expert-backed cryptocurrency investment strategies tailored for long-term growth and stability.
            </p>
            <div className="mt-8 flex justify-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button
                  onClick={() => handlePlanSelect(plans[0])}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary-700 bg-white hover:bg-neutral-100 transition duration-300"
                >
                  Start Your Investment Journey
                  <ChevronRightIcon className="ml-2 h-5 w-5" />
                </button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Stats Section */}
      <div className="relative bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-8 -mt-12 relative z-10">
            <motion.div 
              className="bg-white rounded-lg shadow-lg p-6 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-2xl font-bold text-primary-500">$100M+</p>
              <p className="text-neutral-500">Assets Under Management</p>
            </motion.div>
            <motion.div 
              className="bg-white rounded-lg shadow-lg p-6 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-2xl font-bold text-primary-500">10,000+</p>
              <p className="text-neutral-500">Satisfied Investors</p>
            </motion.div>
            <motion.div 
              className="bg-white rounded-lg shadow-lg p-6 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-2xl font-bold text-primary-500">97%</p>
              <p className="text-neutral-500">Client Satisfaction Rate</p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Investment Plans Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl font-bold text-primary-700 mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Investment Plans
            </motion.h2>
            <motion.div
              className="w-20 h-1 bg-secondary-500 mx-auto mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
            ></motion.div>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Choose your path to financial growth with our transparent, high-yield investment plans
            </p>
          </div>

          {/* First row - 3 basic plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.slice(0, 3).map((plan, index) => (
              <motion.div
                key={index}
                className={`bg-white rounded-xl shadow-lg overflow-hidden transition-transform duration-300 hover:-translate-y-2 relative ${
                  plan.popular ? 'ring-2 ring-primary-500' : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
              >
                {plan.popular && (
                  <div className="bg-secondary-500 text-white px-4 py-1 text-sm font-medium absolute top-0 right-0 z-10">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-primary-700 mb-4">{plan.name}</h3>
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-bold text-primary-800">{plan.price}</span>
                    <span className="ml-2 text-neutral-500">initial</span>
                  </div>
                  <p className="text-neutral-600 mb-6">{plan.type}</p>
                  <div className="border-t border-b border-neutral-200 py-4 mb-6">
                    <p className="text-sm text-neutral-500 mb-1">
                      <span className="font-medium text-primary-700">Duration:</span> {plan.duration}
                    </p>
                    <p className="text-sm text-neutral-500">
                      <span className="font-medium text-primary-700">Support:</span> {plan.support}
                    </p>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0 mt-1" />
                        <span className="text-neutral-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <button
                      onClick={() => handlePlanSelect(plan)}
                      className={`block w-full py-3 px-6 text-center rounded-lg font-medium transition-all duration-200 ${
                        plan.popular
                          ? 'bg-secondary-500 text-white hover:bg-secondary-600'
                          : 'bg-primary-500 text-white hover:bg-primary-600'
                      }`}
                    >
                      Get Started
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Second row - 2 premium plans */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-8">
            {plans.slice(3).map((plan, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden transition-transform duration-300 hover:-translate-y-2"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * (index + 3) }}
              >
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-primary-700 mb-4">{plan.name}</h3>
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-bold text-primary-800">{plan.price}</span>
                    <span className="ml-2 text-neutral-500">initial</span>
                  </div>
                  <p className="text-neutral-600 mb-6">{plan.type}</p>
                  <div className="border-t border-b border-neutral-200 py-4 mb-6">
                    <p className="text-sm text-neutral-500 mb-1">
                      <span className="font-medium text-primary-700">Duration:</span> {plan.duration}
                    </p>
                    <p className="text-sm text-neutral-500">
                      <span className="font-medium text-primary-700">Support:</span> {plan.support}
                    </p>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0 mt-1" />
                        <span className="text-neutral-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <button
                      onClick={() => handlePlanSelect(plan)}
                      className="block w-full py-3 px-6 text-center rounded-lg font-medium transition-all duration-200 bg-primary-500 text-white hover:bg-primary-600"
                    >
                      Get Started
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl font-bold text-primary-700 mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Why Choose WealthQuota
            </motion.h2>
            <motion.div
              className="w-20 h-1 bg-secondary-500 mx-auto mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
            ></motion.div>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              We combine industry expertise with cutting-edge technology to deliver exceptional investment results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <ShieldCheckIcon className="h-10 w-10 text-secondary-500" />,
                title: "Secure & Protected",
                description: "Your investments are protected by industry-leading security measures and advanced encryption technologies."
              },
              {
                icon: <ChartBarIcon className="h-10 w-10 text-secondary-500" />,
                title: "Data-Driven Strategy",
                description: "Our investment decisions are based on comprehensive market analysis and advanced algorithmic trading strategies."
              },
              {
                icon: <CurrencyDollarIcon className="h-10 w-10 text-secondary-500" />,
                title: "Transparent Pricing",
                description: "No hidden fees or unexpected charges. We believe in complete transparency throughout your investment journey."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
              >
                <div className="mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-primary-700 mb-3">{feature.title}</h3>
                <p className="text-neutral-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl font-bold text-primary-700 mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Our Investment Process
            </motion.h2>
            <motion.div
              className="w-20 h-1 bg-secondary-500 mx-auto mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
            ></motion.div>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              A systematic approach to growing your wealth with transparency at every step
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-neutral-200 transform -translate-x-1/2"></div>
            <div className="space-y-16">
              {[
                {
                  step: "01",
                  title: "Account Setup",
                  description: "Choose your investment plan and complete a simple registration process to get started."
                },
                {
                  step: "02",
                  title: "Deposit Funds",
                  description: "Fund your account using our secure payment methods to begin your investment journey."
                },
                {
                  step: "03",
                  title: "Investment Allocation",
                  description: "Our experts allocate your funds across strategic cryptocurrency assets based on market analysis."
                },
                {
                  step: "04",
                  title: "Track & Monitor",
                  description: "Access your dashboard to track performance and monitor your growing portfolio in real-time."
                },
                {
                  step: "05",
                  title: "Withdraw Profits",
                  description: "Enjoy the freedom to withdraw your profits through our convenient withdrawal system."
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index }}
                >
                  <div className="hidden md:block absolute left-1/2 top-4 w-8 h-8 bg-secondary-500 rounded-full -translate-x-1/2 z-10"></div>
                  <div className={`md:flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center`}>
                    <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                      <div className="bg-neutral-50 rounded-xl p-8 md:p-10 shadow-sm">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary-500 text-white font-bold mb-4 md:hidden">
                          {step.step}
                        </div>
                        <h3 className="text-xl font-bold text-primary-700 mb-3">{step.title}</h3>
                        <p className="text-neutral-600">{step.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:block md:w-1/2 text-center">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary-500 text-white text-xl font-bold ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>
                        {step.step}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-primary-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl font-bold mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              What Our Clients Say
            </motion.h2>
            <motion.div
              className="w-20 h-1 bg-secondary-500 mx-auto mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
            ></motion.div>
            <p className="text-lg text-neutral-100 max-w-2xl mx-auto">
              Hear from our satisfied investors about their experience with WealthQuota
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-primary-600 rounded-xl p-6 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
              >
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 rounded-full bg-secondary-500 flex items-center justify-center text-white font-bold">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-white">{testimonial.author}</p>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg 
                          key={i} 
                          className="h-4 w-4 text-secondary-500" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-neutral-100">{testimonial.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl font-bold text-primary-700 mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.div
              className="w-20 h-1 bg-secondary-500 mx-auto mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
            ></motion.div>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Get answers to common questions about our investment process
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                q: "How do you manage investment risks?",
                a: "We employ diversification strategies, expert market analysis, and advanced risk management tools to protect your investments while maximizing potential returns."
              },
              {
                q: "What makes cryptocurrency a good investment?",
                a: "Cryptocurrency offers potential for high returns, acts as an inflation hedge, and provides portfolio diversification. Our expert management helps navigate market volatility."
              },
              {
                q: "How can I track my investment performance?",
                a: "Through our advanced dashboard, you can monitor your investments in real-time, view detailed analytics, and track your portfolio's performance."
              },
              {
                q: "What security measures do you have in place?",
                a: "We implement industry-leading security measures, including secure storage solutions and advanced encryption, to protect your investments."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="bg-neutral-50 rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
              >
                <h3 className="text-lg font-semibold text-primary-700 mb-3">{faq.q}</h3>
                <p className="text-neutral-600">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <motion.h2 
                className="text-3xl font-bold mb-4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                Ready to Start Your Investment Journey?
              </motion.h2>
              <p className="text-neutral-100 mb-6">
                Join thousands of satisfied investors who have transformed their financial future with WealthQuota.
              </p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/signup"
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-secondary-500 text-white font-medium hover:bg-secondary-600 transition duration-300"
                >
                  Get Started Today
                  <ChevronRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
            </div>
            <div className="md:w-1/2 bg-primary-700 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4">Ready to take the next step?</h3>
              <p className="text-neutral-100 mb-4">
                Our investment advisors are available to answer your questions and help you choose the right investment plan.
              </p>
              <div className="flex items-center">
                <svg className="h-5 w-5 text-secondary-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M10 12.5a.5.5 0 01-.5.5h-8a.5.5 0 01-.5-.5V6.5a.5.5 0 01.5-.5h8a.5.5 0 01.5.5v6z" clipRule="evenodd" />
                </svg>
                <span className="ml-2 text-neutral-100">support@wealthquota.com</span>
              </div>
              <div className="flex items-center mt-2">
                <svg className="h-5 w-5 text-secondary-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M10 12a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="ml-2 text-neutral-100">Available 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default InvestmentPlans;